import React from 'react';
import PropTypes from 'prop-types';

import { Select } from 'src/components/common/Select';
import CollectionStore from 'src/stores/alt/stores/CollectionStore';
import CollectionUtils from 'src/models/collection/CollectionUtils';

const collectionOptionValue = ({ id, is_sync_to_me }) => `${is_sync_to_me ? 'sync' : 'collection'}:${id}`;

export default class CopyTargetCollectionSelect extends React.Component {
  constructor(props) {
    super(props);

    const { unsharedRoots, syncInRoots } = CollectionStore.getState();
    this.state = {
      unsharedRoots: unsharedRoots || [],
      syncInRoots: syncInRoots || [],
    };

    this.onCollectionStoreChange = this.onCollectionStoreChange.bind(this);
    this.onSelectChange = this.onSelectChange.bind(this);
  }

  componentDidMount() {
    CollectionStore.listen(this.onCollectionStoreChange);
  }

  componentWillUnmount() {
    CollectionStore.unlisten(this.onCollectionStoreChange);
  }

  onCollectionStoreChange(state) {
    if (
      state.unsharedRoots !== this.state.unsharedRoots
      || state.syncInRoots !== this.state.syncInRoots
    ) {
      this.setState({
        unsharedRoots: state.unsharedRoots || [],
        syncInRoots: state.syncInRoots || [],
      });
    }
  }

  onSelectChange(option) {
    const { onChange } = this.props;
    if (!option) {
      onChange(null);
      return;
    }

    onChange({
      id: option.id,
      label: option.label,
      is_sync_to_me: option.is_sync_to_me,
    });
  }

  makeOptions(collections, isSync = false, options = [], depth = 0) {
    if (!Array.isArray(collections)) return options;

    collections.forEach((collection) => {
      if (collection.label === 'All') return;

      options.push({
        value: collectionOptionValue({ id: collection.id, is_sync_to_me: isSync }),
        id: collection.id,
        label: collection.label,
        depth,
        is_sync_to_me: isSync,
      });
      this.makeOptions(collection.children, isSync, options, depth + 1);
    });

    return options;
  }

  makeSyncOptions() {
    const { syncInRoots } = this.state;
    const writableSyncCollections = syncInRoots
      .flatMap((syncInRoot) => syncInRoot.children || [])
      .filter(CollectionUtils.isWritable);

    return this.makeOptions(writableSyncCollections, true);
  }

  groupedOptions() {
    const { includeSyncCollections } = this.props;
    const { unsharedRoots } = this.state;
    const collectionOptions = this.makeOptions(unsharedRoots);

    if (!includeSyncCollections) return collectionOptions;

    const syncOptions = this.makeSyncOptions();
    return [
      {
        label: 'My Collections',
        options: collectionOptions,
      },
      {
        label: 'Synchronized Collections',
        options: syncOptions,
      },
    ];
  }

  render() {
    const { value } = this.props;
    const options = this.groupedOptions();
    const flatOptions = options.flatMap((option) => option.options || [option]);

    const optionLabel = ({ label, depth }) => (
      <span style={{ paddingLeft: `${depth * 10}px` }}>
        {label}
      </span>
    );

    return (
      <Select
        id="modal-collection-id-select"
        options={options}
        formatOptionLabel={optionLabel}
        value={flatOptions.find((option) => (
          value
          && option.id === value.id
          && option.is_sync_to_me === value.is_sync_to_me
        ))}
        onChange={this.onSelectChange}
      />
    );
  }
}

CopyTargetCollectionSelect.propTypes = {
  value: PropTypes.shape({
    id: PropTypes.number.isRequired,
    label: PropTypes.string,
    is_sync_to_me: PropTypes.bool.isRequired,
  }),
  onChange: PropTypes.func.isRequired,
  includeSyncCollections: PropTypes.bool,
};

CopyTargetCollectionSelect.defaultProps = {
  value: null,
  includeSyncCollections: false,
};
